import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { getCandidateFeedbackOptions, type CandidateFeedbackItem } from '@/api';
import { formatCommentDate } from '@/components/ApplicationComments/format';
import { seesCandidateFeedback } from '@/lib/sessionCrm';
import useUser from '@/hooks/useUser';

const PAGE_SIZE = 20;

export const Route = createFileRoute('/_auth/feedback')({
  component: FeedbackPage,
  validateSearch: (search: Record<string, unknown>): { page?: number } => {
    const page = Number(search.page);
    return { page: Number.isInteger(page) && page > 1 ? page : undefined };
  },
});

function FeedbackPage() {
  const { user } = useUser();

  if (user && !seesCandidateFeedback(user)) {
    return <Navigate to="/dashboard" />;
  }

  return <FeedbackList />;
}

function FeedbackList() {
  const { page = 1 } = Route.useSearch();
  const { data, isLoading, isError } = useQuery({
    ...getCandidateFeedbackOptions({ query: { page, page_size: PAGE_SIZE } }),
    // Keep the current page on screen while the next one loads
    placeholderData: keepPreviousData,
  });

  const total = data?.total ?? 0;
  const totalPages = data?.total_pages ?? 0;
  const first = (page - 1) * PAGE_SIZE + 1;
  const last = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="max-w-2xl">
      <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-neutral-900 md:text-[30px]">
        Feedback
      </h1>
      <p className="mt-1 text-sm leading-6 text-neutral-600">
        What candidates said after their session
      </p>

      {isLoading ? (
        <div className="mt-6 space-y-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <p className="mt-6 text-sm text-neutral-600">Couldn't load feedback. Try again in a moment.</p>
      ) : total === 0 ? (
        <p className="mt-6 text-sm text-neutral-600">No feedback yet</p>
      ) : (
        <>
          <ul className="mt-4 divide-y divide-neutral-100">
            {data?.items.map((item) => <FeedbackRow key={item.id} item={item} />)}
          </ul>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between gap-3">
              <span className="text-sm tabular-nums text-neutral-500">
                {first}–{last} of {total}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} asChild={page > 1}>
                  {page > 1 ? (
                    <Link to="/feedback" search={{ page: page - 1 > 1 ? page - 1 : undefined }}>
                      Previous
                    </Link>
                  ) : (
                    <span>Previous</span>
                  )}
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} asChild={page < totalPages}>
                  {page < totalPages ? (
                    <Link to="/feedback" search={{ page: page + 1 }}>
                      Next
                    </Link>
                  ) : (
                    <span>Next</span>
                  )}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FeedbackRow({ item }: { item: CandidateFeedbackItem }) {
  const name = [item.first_name, item.last_name].filter(Boolean).join(' ') || item.email || 'Candidate';

  return (
    <li>
      <Link
        to="/applications/$sessionId"
        params={{ sessionId: item.session_template_id }}
        search={{ application: item.application_id }}
        className="-mx-3 block rounded-xl px-3 py-3 transition-colors hover:bg-neutral-50">
        <p className="text-[15px] leading-6 text-neutral-900 break-words">{item.comment}</p>
        <div className="mt-1 flex items-center gap-2 text-[13px] text-neutral-500">
          <span className="min-w-0 truncate">
            {name}
            {item.session_name ? ` · ${item.session_name}` : ''}
          </span>
          <span className="shrink-0">· {formatCommentDate(item.created_at)}</span>
          {item.rating ? <Rating value={item.rating} /> : null}
        </div>
      </Link>
    </li>
  );
}

function Rating({ value }: { value: number }) {
  return (
    <span className="ml-auto flex shrink-0 items-center gap-0.5" aria-label={`Rated ${value} of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={i < value ? 'h-3 w-3 fill-amber-400 text-amber-400' : 'h-3 w-3 text-neutral-300'}
        />
      ))}
    </span>
  );
}
